<template>
  <div class="order-detail-container">
    <!-- Professional Header -->
    <div class="page-header">
      <div class="header-top">
        <button @click="goBack" class="back-button">
          <i class="fas fa-arrow-left"></i>
          <span>Zurück zur Übersicht</span>
        </button>
        <div class="header-actions">
          <button v-if="canEdit" class="btn-action btn-edit" @click="editOrder">
            <i class="fas fa-edit"></i>
            <span>Bearbeiten</span>
          </button>
          <button class="btn-action btn-print" @click="openPdf('tasche')">
            <i class="fas fa-file-pdf"></i>
            <span>Tasche</span>
          </button>
          <button class="btn-action btn-print" @click="openPdf('bockzettel')">
            <i class="fas fa-qrcode"></i>
            <span>Bockzettel</span>
          </button>
          <button class="btn-action btn-print" @click="openPdf('protokoll')">
            <i class="fas fa-stopwatch"></i>
            <span>Protokoll</span>
          </button>
          <button
              v-if="order.delivery && canEdit && order.status !== 'cancelled'"
              class="btn-action btn-versand"
              @click="openVersand"
          >
            <i class="fas fa-truck"></i>
            <span>Versand erfassen</span>
          </button>
          <button
              v-if="canManage && order.status !== 'cancelled'"
              class="btn-action btn-storno"
              @click="showStorno = true"
          >
            <i class="fas fa-ban"></i>
            <span>Stornieren</span>
          </button>
        </div>
      </div>

      <div class="header-main">
        <div class="order-info">
          <div class="order-icon">
            <i class="fas fa-file-invoice"></i>
          </div>
          <div>
            <h1 class="order-title">
              Auftrag {{ order.order_number }}
              <span v-if="order.invoice_number" class="invoice-suffix">Rechnung: {{ order.invoice_number }}</span>
            </h1>
            <div class="order-meta">
              <span v-if="customer" class="meta-item">
                <i class="fas fa-building"></i>
                <router-link :to="`/kunden/${customer.id}`" class="customer-link">
                  {{ customer.name }}
                </router-link>
              </span>
              <span v-if="employee" class="meta-item">
                <i class="fas fa-user"></i>
                <router-link :to="`/mitarbeiter/${employee.id}`" class="customer-link">
                  {{ employee.name }}
                </router-link>
              </span>
              <span v-else-if="order.employee_name" class="meta-item">
                <i class="fas fa-user"></i>
                {{ order.employee_name }}
              </span>
              <span v-if="order.created_at" class="meta-item">
                <i class="fas fa-calendar"></i>
                {{ formatDate(order.created_at) }}
              </span>
            </div>
          </div>
        </div>
        <div class="status-badges">
          <order-comment-popup v-if="order.id" :order="order" @updated="onCommentUpdated" />
          <order-flags v-if="order.id" :order="order" class="header-flags" />
        </div>
      </div>
    </div>

    <!-- WTV / Fremd-Weiterverarbeitung banner (#3) -->
    <div
      v-if="showWtvBanner"
      class="wtv-banner"
      role="alert"
    >
      <i class="fas fa-exclamation-triangle"></i>
      <div class="wtv-content">
        <strong>Fremd-Weiterverarbeitung (WTV)</strong>
        <div class="wtv-text">{{ wtvText }}</div>
      </div>
      <button class="wtv-dismiss" @click="dismissWtv" title="Hinweis ausblenden">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Lädt...</span>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="content-grid">
      <!-- Left Column: Order Details -->
      <div class="details-section">
        <!-- Production Details -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-print"></i>
            <h3>Produktionsdetails</h3>
          </div>
          <div class="info-card-body">
            <div v-if="order.description" class="info-row">
              <span class="info-label">Beschreibung</span>
              <span class="info-value">{{ order.description }}</span>
            </div>
            <div v-if="order.customer_order_number" class="info-row">
              <span class="info-label">Kunden-Bestellnr.</span>
              <span class="info-value">{{ order.customer_order_number }}</span>
            </div>
            <div v-if="order.print_run" class="info-row">
              <span class="info-label">Auflage</span>
              <span class="info-value">{{ formatNumber(order.print_run) }} Stück</span>
            </div>
            <div v-if="order.page_count" class="info-row">
              <span class="info-label">Seitenzahl</span>
              <span class="info-value">{{ order.page_count }}</span>
            </div>
            <div v-if="order.druckform_count" class="info-row">
              <span class="info-label">Druckformen / Durchgänge</span>
              <span class="info-value">{{ order.druckform_count }}</span>
            </div>
            <div v-if="order.format" class="info-row">
              <span class="info-label">Format</span>
              <span class="info-value">{{ order.format }}</span>
            </div>
            <div v-if="order.final_width && order.final_length" class="info-row">
              <span class="info-label">Endformat</span>
              <span class="info-value">{{ order.final_width }} × {{ order.final_length }} cm</span>
            </div>
            <div v-if="order.print_width && order.print_length" class="info-row">
              <span class="info-label">Druckformat</span>
              <span class="info-value">{{ order.print_width }} × {{ order.print_length }} cm</span>
            </div>
            <div v-if="order.color" class="info-row">
              <span class="info-label">Farbe</span>
              <span class="info-value">
                <farbigkeit-square :value="order.farbigkeit || order.color" />
                <span class="ms-2">{{ order.color }}</span>
              </span>
            </div>
            <div v-if="order.raster_output" class="info-row">
              <span class="info-label">Raster</span>
              <span class="info-value">{{ rasterLabel }}</span>
            </div>
            <div v-if="order.maschine_display || order.maschine_name" class="info-row">
              <span class="info-label">Maschine</span>
              <span class="info-value">{{ order.maschine_display || order.maschine_name }}</span>
            </div>
            <div v-if="order.druckart_codes && order.druckart_codes.length" class="info-row">
              <span class="info-label">Druckart</span>
              <span class="info-value">
                <span v-for="c in order.druckart_codes" :key="c" class="druckart-badge">{{ c }}</span>
              </span>
            </div>
            <div v-if="order.position_code" class="info-row">
              <span class="info-label">Position</span>
              <span class="info-value">
                {{ order.position_code }}
                <template v-if="order.position_template">
                  — {{ order.position_template.description }}
                  <span v-if="order.position_template.time_minutes">({{ order.position_template.time_minutes }} min)</span>
                </template>
              </span>
            </div>
          </div>
        </div>

        <!-- Paper Details -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-scroll"></i>
            <h3>Papier</h3>
          </div>
          <div class="info-card-body">
            <div v-if="order.paper_type" class="info-row">
              <span class="info-label">Papiertyp</span>
              <span class="info-value">{{ order.paper_type }}</span>
            </div>
            <div v-if="order.paper_weight" class="info-row">
              <span class="info-label">Grammatur</span>
              <span class="info-value">{{ order.paper_weight }} g/m²</span>
            </div>
            <div v-if="order.material_status" class="info-row">
              <span class="info-label">Materialstatus</span>
              <span :class="['info-value', 'material-badge', getMaterialStatusClass(order.material_status)]">
                {{ order.material_status }}
              </span>
            </div>
          </div>
        </div>

        <!-- Dates & Deadlines -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-calendar-alt"></i>
            <h3>Termine</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Liefertermin</span>
              <span class="info-value highlight">
                <i class="fas fa-truck"></i>
                {{ formatDeliveryDate(order) }}
              </span>
            </div>
            <div v-if="order.print_year" class="info-row">
              <span class="info-label">Drucktermin</span>
              <span class="info-value">{{ formatPrintDate(order) }}</span>
            </div>
            <div v-if="order.prepress_at" class="info-row">
              <span class="info-label">Vorstufe</span>
              <span class="info-value">{{ formatDateTime(order.prepress_at) }}</span>
            </div>
            <div v-if="order.plates_at" class="info-row">
              <span class="info-label">Platten</span>
              <span class="info-value">{{ formatDateTime(order.plates_at) }}</span>
            </div>
            <div v-if="order.printing_at" class="info-row">
              <span class="info-label">Druck</span>
              <span class="info-value">{{ formatDateTime(order.printing_at) }}</span>
            </div>
            <div v-if="order.processing_at" class="info-row">
              <span class="info-label">Weiterverarbeitung</span>
              <span class="info-value">{{ formatDateTime(order.processing_at) }}</span>
            </div>
            <div v-if="order.shipping_at" class="info-row">
              <span class="info-label">Versand</span>
              <span class="info-value">{{ formatDateTime(order.shipping_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Payment & Shipping -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-credit-card"></i>
            <h3>Zahlung & Versand</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Zahlungsstatus</span>
              <span :class="['payment-badge', getPaymentStatusClass(order.payment_status)]">
                {{ getPaymentStatusText(order.payment_status) }}
              </span>
            </div>
            <div v-if="order.delivery_method !== null" class="info-row">
              <span class="info-label">Versandart</span>
              <span class="info-value">{{ getDeliveryMethodText(order.delivery_method) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Standort</span>
              <span class="info-value">
                <standort-logo :order-number="order.order_number" :pk-sb="order.pk_sb" />
              </span>
            </div>


          </div>
        </div>

        <!-- Lieferung -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-truck"></i>
            <h3>Lieferung</h3>
          </div>
          <div class="info-card-body">
            <div v-if="!order.delivery" class="no-delivery-banner">
              <i class="fas fa-exclamation-triangle"></i>
              <span>Kein Lieferdatensatz vorhanden</span>
            </div>
            <template v-else>
              <div class="info-row">
                <span class="info-label">Lieferadresse</span>
                <span class="info-value">{{ order.delivery.shipping_address || '—' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Versandbemerkung</span>
                <span class="info-value">{{ order.delivery.shipping_notes || '—' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Versandart</span>
                <span class="info-value">{{ order.delivery.delivery_method_text || '—' }}</span>
              </div>
              <div class="info-row">
                <router-link :to="`/lieferungen/${order.delivery.id}`" class="info-link">
                  <i class="fas fa-arrow-right"></i> Zur Lieferung
                </router-link>
              </div>
            </template>
          </div>
        </div>

        <!-- Notes -->
        <div v-if="order.notes" class="info-card">
          <div class="info-card-header">
            <i class="fas fa-sticky-note"></i>
            <h3>Notizen</h3>
          </div>
          <div class="info-card-body">
            <p class="notes-text">{{ order.notes }}</p>
          </div>
        </div>

        <!-- Handnotiz (#21) -->
        <div v-if="order.handnotiz" class="info-card">
          <div class="info-card-header">
            <i class="fas fa-pen"></i>
            <h3>Handnotiz</h3>
          </div>
          <div class="info-card-body">
            <p class="notes-text">{{ order.handnotiz }}</p>
          </div>
        </div>

        <!-- Admin-Kommentar zu Prinance-Daten (#22) -->
        <div v-if="order.prinance_admin_comment" class="info-card">
          <div class="info-card-header">
            <i class="fas fa-user-shield"></i>
            <h3>Admin-Kommentar (Prinance)</h3>
          </div>
          <div class="info-card-body">
            <p class="notes-text">{{ order.prinance_admin_comment }}</p>
          </div>
        </div>
      </div>

      <!-- Right Column: Customer & Contacts -->
      <div class="relations-section">
        <!-- Customer Card -->
        <div class="info-card" v-if="customer">
          <div class="info-card-header">
            <i class="fas fa-building"></i>
            <h3>Kunde</h3>
          </div>
          <div class="info-card-body">
            <div class="customer-header">
              <h4>
                <router-link :to="`/kunden/${customer.id}`" class="customer-name-link">
                  {{ customer.name }}
                </router-link>
              </h4>
              <span v-if="customer.name_suffix" class="customer-suffix">{{ customer.name_suffix }}</span>
            </div>
            <div v-if="customer.address" class="info-row">
              <span class="info-label">Anschrift</span>
              <span class="info-value">{{ customer.address }}</span>
            </div>
            <div v-if="customer.postal_code || customer.city" class="info-row">
              <span class="info-label">Ort</span>
              <span class="info-value">{{ customer.postal_code }} {{ customer.city }}</span>
            </div>
            <div v-if="customer.phone" class="info-row">
              <span class="info-label">Telefon</span>
              <a :href="`tel:${customer.phone}`" class="info-value info-link">
                <i class="fas fa-phone fa-xs"></i>
                {{ customer.phone }}
              </a>
            </div>
            <div v-if="customer.email" class="info-row">
              <span class="info-label">E-Mail</span>
              <a :href="`mailto:${customer.email}`" class="info-value info-link">
                <i class="fas fa-envelope fa-xs"></i>
                {{ customer.email }}
              </a>
            </div>
          </div>
        </div>

        <!-- Bearbeiter Card -->
        <div class="info-card" v-if="employee || canEdit">
          <div class="info-card-header">
            <i class="fas fa-user-tie"></i>
            <h3>Bearbeiter</h3>
          </div>
          <div class="info-card-body">
            <div v-if="employee" class="customer-header">
              <h4>
                <router-link :to="`/mitarbeiter/${employee.id}`" class="customer-name-link">
                  {{ employee.name }}
                </router-link>
              </h4>
            </div>
            <!-- Inline zuweisen/ändern direkt in der Detailansicht (Q-1) — kein
                 Umweg mehr über das Bearbeiten-Formular. -->
            <div v-if="canEdit" class="info-row">
              <span class="info-label">{{ employee ? 'Ändern' : 'Zuweisen' }}</span>
              <filter-dropdown
                :model-value="order.employee_id != null ? String(order.employee_id) : ''"
                :options="employeeOptions()"
                label="Bearbeiter"
                placeholder="Nicht zugewiesen"
                :show-avatars="false"
                :block="true"
                :disabled="assigningEmployee"
                @update:modelValue="assignEmployee"
              />
            </div>
            <div v-if="employee && employee.abteilung" class="info-row">
              <span class="info-label">Abteilung</span>
              <span class="info-value">{{ employee.abteilung }}</span>
            </div>
            <div v-if="employee && employee.tel" class="info-row">
              <span class="info-label">Telefon</span>
              <a :href="`tel:${employee.tel}`" class="info-value info-link">
                <i class="fas fa-phone fa-xs"></i>
                {{ employee.tel }}
              </a>
            </div>
            <div v-if="employee && employee.mobil" class="info-row">
              <span class="info-label">Mobil</span>
              <a :href="`tel:${employee.mobil}`" class="info-value info-link">
                <i class="fas fa-mobile-alt fa-xs"></i>
                {{ employee.mobil }}
              </a>
            </div>
            <div v-if="employee && employee.email" class="info-row">
              <span class="info-label">E-Mail</span>
              <a :href="`mailto:${employee.email}`" class="info-value info-link">
                <i class="fas fa-envelope fa-xs"></i>
                {{ employee.email }}
              </a>
            </div>
          </div>
        </div>

        <!-- Contacts Card -->
        <div class="info-card" v-if="contacts && contacts.length > 0">
          <div class="info-card-header">
            <i class="fas fa-users"></i>
            <h3>Ansprechpartner</h3>
          </div>
          <div class="info-card-body">
            <div v-for="contact in contacts" :key="contact.id" class="contact-item">
              <div class="contact-name">
                <strong>{{ contact.salutation }} {{ contact.first_name }} {{ contact.last_name }}</strong>
              </div>
              <div v-if="contact.department" class="contact-department">
                {{ contact.department }}
              </div>
              <div class="contact-details">
                <a v-if="contact.phone" :href="`tel:${contact.phone}`" class="contact-link">
                  <i class="fas fa-phone fa-xs"></i> {{ contact.phone }}
                </a>
                <a v-if="contact.mobile" :href="`tel:${contact.mobile}`" class="contact-link">
                  <i class="fas fa-mobile-alt fa-xs"></i> {{ contact.mobile }}
                </a>
                <a v-if="contact.email" :href="`mailto:${contact.email}`" class="contact-link">
                  <i class="fas fa-envelope fa-xs"></i> {{ contact.email }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Paper Orders Card -->
        <div class="info-card" v-if="paperOrders && paperOrders.length > 0">
          <div class="info-card-header">
            <i class="fas fa-boxes"></i>
            <h3>Papierbestellungen</h3>
          </div>
          <div class="info-card-body">
            <div v-for="paper in paperOrders" :key="paper.id" class="paper-item">
              <div class="paper-designation">{{ paper.designation }}</div>
              <div class="paper-details">
                <span v-if="paper.sheets_gross">{{ formatNumber(paper.sheets_gross) }} Bogen</span>
                <span v-if="paper.width && paper.length">{{ paper.width }} × {{ paper.length }} cm</span>
                <span v-if="paper.weight">{{ paper.weight }} g/m²</span>
              </div>
              <div v-if="paper.supplier" class="paper-supplier">
                <i class="fas fa-truck"></i> {{ paper.supplier }}
              </div>
              <div v-if="paper.delivery_date" class="paper-date">
                Lieferung: {{ formatDate(paper.delivery_date) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <order-time-panel
        v-if="order.id"
        :order="order"
        :can-edit="canEdit"
        class="detail-shift-panel"
        @updated="loadOrder"
      />

      <!-- A-4 — Protokoll / Zeiten (Kalk-vs-Ist + Zeitstempel) -->
      <div v-if="order.id" class="info-card detail-shift-panel protokoll-card">
        <div class="info-card-header">
          <i class="fas fa-stopwatch"></i>
          <h3>Protokoll / Zeiten</h3>
          <button class="protokoll-pdf" @click="openPdf('protokoll')">
            <i class="fas fa-file-pdf"></i><span>PDF</span>
          </button>
        </div>
        <div class="info-card-body protokoll-cols">
          <div class="protokoll-block">
            <h4>Kalk. vs. Ist — Zeiten je Etappe</h4>
            <table class="protokoll-table">
              <tr v-for="row in timeReport" :key="row.stage">
                <td>{{ row.label }}</td>
                <td class="pt-val">{{ fmtDuration(row.ist_seconds) }}</td>
              </tr>
              <tr class="pt-total">
                <td>Ist gesamt</td>
                <td class="pt-val">{{ fmtDuration(istTotalSeconds) }}</td>
              </tr>
              <tr class="pt-total">
                <td>Kalk. gesamt</td>
                <td class="pt-val">{{ kalkSeconds != null ? fmtDuration(kalkSeconds) : '—' }}</td>
              </tr>
            </table>
          </div>
          <div class="protokoll-block">
            <h4>Zeitstempel-Protokoll</h4>
            <table class="protokoll-table">
              <tr><td>Auftrag erteilt</td><td class="pt-val">{{ formatDateTime(order.created_at) }}</td></tr>
              <tr><td>Vorstufe fertig</td><td class="pt-val">{{ formatDateTime(order.prepress_at) }}</td></tr>
              <tr><td>Platten fertig</td><td class="pt-val">{{ formatDateTime(order.plates_at) }}</td></tr>
              <tr><td>Druck fertig</td><td class="pt-val">{{ formatDateTime(order.printing_at) }}</td></tr>
              <tr><td>WTV fertig</td><td class="pt-val">{{ formatDateTime(order.processing_at) }}</td></tr>
              <tr><td>Versand fertig</td><td class="pt-val">{{ formatDateTime(order.shipping_at) }}</td></tr>
              <tr><td>Ausgeliefert</td><td class="pt-val">{{ order.delivery && order.delivery.delivered_on ? formatDateTime(order.delivery.delivered_on) : '-' }}</td></tr>
              <tr><td>Rechnung</td><td class="pt-val">{{ order.invoice_number || '-' }}</td></tr>
            </table>
          </div>
        </div>
      </div>

      <shift-production-panel
        v-if="order.id"
        :order-id="order.id"
        class="detail-shift-panel"
        @updated="onShiftUpdated"
      />

      <change-history item-type="Order" :item-id="order.id" />
    </div>

    <!-- Storno-Dialog (#44) -->
    <div v-if="showStorno" class="modal-overlay" @click.self="showStorno = false">
      <div class="storno-modal">
        <h3>Auftrag stornieren</h3>
        <p>Der Auftrag <strong>{{ order.order_number }}</strong> wird storniert und verschwindet aus den aktiven Listen. Der Vorgang wird protokolliert.</p>
        <label>Stornogrund <span class="req">*</span></label>
        <textarea v-model="stornoGrund" rows="3" placeholder="Grund eingeben …"></textarea>
        <p v-if="stornoError" class="storno-err">{{ stornoError }}</p>
        <div class="storno-actions">
          <button class="btn-cancel" @click="showStorno = false">Abbrechen</button>
          <button class="btn-confirm" :disabled="!stornoGrund.trim() || cancelling" @click="confirmStorno">
            Stornieren
          </button>
        </div>
      </div>
    </div>

    <!-- Versand-/Postausgabebuch-Maske (#18) — öffnet automatisch bei „Versand fertig" -->
    <div v-if="showVersand" class="modal-overlay" @click.self="showVersand = false">
      <div class="storno-modal versand-modal">
        <h3>Versand erfassen — {{ order.order_number }}</h3>
        <p>Postausgabebuch-Eintrag für <strong>{{ customer ? customer.name : '' }}</strong>.</p>

        <label>Versandart</label>
        <div class="versand-methods">
          <label v-for="opt in deliveryMethodOptions" :key="opt.value" class="vm-radio">
            <input type="radio" :value="opt.value" v-model.number="versandForm.delivery_method" />
            {{ opt.text }}
          </label>
        </div>

        <div class="versand-grid">
          <label>Kürzel Mitarbeiter
            <input v-model="versandForm.versand_kuerzel" maxlength="8" />
          </label>
          <label>Verpackung
            <input v-model="versandForm.packaging" placeholder="z.B. 2 Kartons/Palette" />
          </label>
          <label>Bestellte Stückzahl
            <input v-model.number="versandForm.ordered_quantity" type="number" min="0" />
          </label>
          <label>Gelieferte Stückzahl
            <input v-model.number="versandForm.delivered_quantity" type="number" min="0" />
          </label>
          <label class="full">Tracking-Nr.
            <input v-model="versandForm.tracking_nr" placeholder="optional" />
          </label>
        </div>

        <div class="storno-actions">
          <button class="btn-cancel" @click="showVersand = false">Abbrechen</button>
          <button class="btn-confirm" :disabled="versandSaving" @click="saveVersand">
            {{ versandSaving ? 'Speichert …' : 'Speichern' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatNumberSmart } from '@/utils/numberFormat'
import api from '@/api'
import FarbigkeitSquare from '@/components/FarbigkeitSquare.vue'
import OrderFlags from '@/components/OrderFlags.vue'
import OrderCommentPopup from '@/components/OrderCommentPopup.vue'
import ChangeHistory from '@/components/ChangeHistory.vue'
import ShiftProductionPanel from '@/components/ShiftProductionPanel.vue'
import OrderTimePanel from '@/components/OrderTimePanel.vue'
import StandortLogo from '@/components/StandortLogo.vue'
import FilterDropdown from '@/components/FilterDropdown.vue'
import { ORDER_CONSTANTS } from '@/services/orderService.js'

export default {
  name: 'OrderShow',
  components: { FarbigkeitSquare, OrderFlags, OrderCommentPopup, ChangeHistory, ShiftProductionPanel, OrderTimePanel, StandortLogo, FilterDropdown },

  data() {
    return {
      order: {},
      customer: null,
      employee: null,
      contacts: [],
      paperOrders: [],
      timeReport: [],   // A-4 — Ist-Netto je Etappe
      kalkSeconds: null, // A-4 — Gesamt-Planzeit
      employeeList: [],
      assigningEmployee: false,
      wtvDismissed: false,
      showStorno: false,
      stornoGrund: '',
      stornoError: null,
      cancelling: false,
      // Versand/Postausgabebuch (#18)
      showVersand: false,
      versandForm: {},
      versandSaving: false,
      _versandAutoShown: false
    }
  },

  computed: {
    ...mapGetters(['loading', 'user', 'can']),

    canEdit() {
      if (!this.user || !this.order.id) return false
      return this.can('edit_any_order') || this.order.employee_id === this.user.id
    },

    // A-4 — Summe der Ist-Netto-Sekunden aller Etappen.
    istTotalSeconds() {
      return (this.timeReport || []).reduce((sum, r) => sum + (r.ist_seconds || 0), 0)
    },

    canManage() {
      return this.can('delete_orders')
    },

    // #3 WTV/Fremd-Weiterverarbeitung reminder. Triggered by status reaching
    // processing_complete (WTV-Etappe) OR notes mentioning WTV/extern. Stays
    // until the user dismisses it (per-session, not persisted).
    wtvText() {
      const notes = (this.order.notes || '').toString()
      const m = notes.match(/wtv[:\s-]*([^\n.]{1,180})/i)
        || notes.match(/(?:extern|fremd)[:\s-]*([^\n.]{1,180})/i)
      if (m && m[1]) return m[1].trim()
      return 'Bitte vor Auslieferung an Fremd-WV (z.B. Bubi WV ex) übergeben.'
    },
    showWtvBanner() {
      if (this.wtvDismissed) return false
      if (this.order.status === 'processing_complete') return true
      const notes = (this.order.notes || '').toString().toLowerCase()
      return /\bwtv\b/.test(notes) || /\bfremd-?w?v?\b/.test(notes)
    },

    deliveryMethodOptions() {
      return ORDER_CONSTANTS.DELIVERY_METHOD_OPTIONS || []
    },

    // A-5 — "70" is stored, "70er" is what the shop floor says. Unknown values
    // (Prinance LE 38 is free text) are shown verbatim, never re-suffixed.
    rasterLabel() {
      const v = this.order.raster_output
      if (!v) return ''
      const hit = (ORDER_CONSTANTS.RASTER_OPTIONS || []).find(o => String(o.value) === String(v))
      return hit ? hit.label : String(v)
    }
  },

  watch: {
    // Auto-Popup: bei „Versand fertig" (shipping_complete) und noch nicht
    // abgeschlossener Lieferung die Versand-/Postausgabebuch-Maske öffnen (#18).
    'order.status'(status) {
      if (status === 'shipping_complete' && this.order.delivery && !this.order.delivery.finalized
          && this.canEdit && !this._versandAutoShown) {
        this._versandAutoShown = true
        this.openVersand()
      }
    }
  },

  async mounted() {
    await Promise.all([this.loadOrder(), this.loadEmployees()])
    // Archivierten (aber zugewiesenen) Bearbeiter nachziehen, damit er im
    // Dropdown weiter auswählbar bleibt (Muster aus EditView).
    if (this.employee && !this.employeeList.find(e => e.id === this.employee.id)) {
      this.employeeList = [...this.employeeList, this.employee]
    }
  },

  methods: {
    ...mapActions(['fetchData']),

    async loadOrder() {
      try {
        const orderId = this.$route.params.id
        const response = await this.fetchData({
          endpoint: `orders/${orderId}`
        })

        if (response) {
          this.order = response.order
          this.customer = response.customer
          this.employee = response.employee
          this.contacts = response.contacts || []
          this.paperOrders = response.paper_orders || []
          this.timeReport = response.time_report || []
          this.kalkSeconds = response.kalk_seconds ?? null
        }
      } catch (error) {
        console.error('Error loading order:', error)
        this.$router.push('/auftraege')
      }
    },

    async loadEmployees() {
      try {
        const response = await this.fetchData({ endpoint: 'users' })
        this.employeeList = response.data || response || []
      } catch (err) {
        console.error('Failed to load employees:', err)
      }
    },

    employeeOptions() {
      return this.employeeList.map(e => ({ value: String(e.id), label: e.name, icon_url: e.icon_url, id: e.id }))
    },

    // Q-1: Bearbeiter direkt aus der Detailansicht zuweisen/ändern. PUT wie in
    // EditView (employee_id ist bereits erlaubt + authorize_edit!), danach den
    // Auftrag neu laden, damit die Bearbeiter-Karte + Kopfzeile aktuell sind.
    async assignEmployee(val) {
      const employeeId = val ? parseInt(val) : null
      if (employeeId === (this.order.employee_id != null ? this.order.employee_id : null)) return
      this.assigningEmployee = true
      try {
        await this.fetchData({
          endpoint: `orders/${this.order.id}`,
          method: 'PUT',
          params: { order: { employee_id: employeeId } }
        })
        await this.loadOrder()
        this.$toast?.success('Bearbeiter aktualisiert')
      } catch (e) {
        console.error('Bearbeiter-Zuweisung fehlgeschlagen:', e)
        this.$toast?.error('Bearbeiter konnte nicht zugewiesen werden')
      } finally {
        this.assigningEmployee = false
      }
    },

    goBack() {
      this.$router.push('/auftraege')
    },

    editOrder() {
      this.$router.push(`/auftraege/${this.order.id}/edit`)
    },

    dismissWtv() {
      this.wtvDismissed = true
    },

    onCommentUpdated(text) {
      this.order = { ...this.order, active_comment: text }
    },

    // #16 — keep the header totals in sync after a Schicht-Erfassung.
    onShiftUpdated({ producedToday, producedTotal }) {
      this.order = { ...this.order, produced_today: producedToday, produced_total: producedTotal }
    },

    async openPdf(kind) {
      try {
        await api.openPdf(`orders/${this.order.id}/${kind}`)
      } catch (e) {
        this.$toast?.error('PDF konnte nicht erzeugt werden')
      }
    },

    async confirmStorno() {
      this.cancelling = true
      this.stornoError = null
      try {
        await api.post(`orders/${this.order.id}/cancel`, { grund: this.stornoGrund.trim() })
        this.showStorno = false
        this.$toast?.success('Auftrag storniert')
        await this.loadOrder()
      } catch (e) {
        this.stornoError = e.message || 'Stornieren fehlgeschlagen'
      } finally {
        this.cancelling = false
      }
    },

    // ── Versand/Postausgabebuch (#18) ──────────────────────────────────────
    openVersand() {
      const d = this.order.delivery || {}
      this.versandForm = {
        delivery_method: d.delivery_method != null ? d.delivery_method : 0,
        packaging: d.packaging || '',
        ordered_quantity: d.ordered_quantity != null ? d.ordered_quantity : (this.order.print_run || null),
        delivered_quantity: d.delivered_quantity != null ? d.delivered_quantity : (this.order.print_run || null),
        tracking_nr: d.tracking_nr || '',
        versand_kuerzel: d.versand_kuerzel || (this.user && this.user.kuerzel) || ''
      }
      this.showVersand = true
    },
    async saveVersand() {
      const d = this.order.delivery
      if (!d || !d.id) { this.$toast?.error('Keine Lieferung zum Auftrag vorhanden'); return }
      this.versandSaving = true
      try {
        await api.put(`delivery/${d.id}`, { delivery: this.versandForm })
        await api.post(`delivery/${d.id}/finalize`) // stempelt delivered_on + Kürzel
        this.$toast?.success('Versand erfasst')
        this.showVersand = false
        await this.loadOrder()
      } catch (e) {
        console.error('Versand speichern fehlgeschlagen:', e)
        this.$toast?.error('Versand konnte nicht gespeichert werden')
      } finally {
        this.versandSaving = false
      }
    },

    // Formatting methods
    // Uses smart formatter: "50000" → "50.000", "50x" stays "50x", empty → "-".
    formatNumber(value) {
      const out = formatNumberSmart(value)
      return out === '' ? '-' : out
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('de-DE')
    },

    formatDateTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('de-DE')
    },

    // A-4 — Sekunden → "H:MM".
    fmtDuration(sec) {
      const s = Math.max(0, Math.round(sec || 0))
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      return `${h}:${String(m).padStart(2, '0')}`
    },

    formatDeliveryDate(order) {
      if (!order.delivery_year) return '-'
      const date = `${order.delivery_day}.${order.delivery_month}.${order.delivery_year}`
      if (order.delivery_hour) {
        return `${date} um ${order.delivery_hour}:00 Uhr`
      }
      return date
    },

    formatPrintDate(order) {
      if (!order.print_year) return '-'
      const date = `${order.print_day}.${order.print_month}.${order.print_year}`
      if (order.print_hour) {
        return `${date} um ${order.print_hour}:00 Uhr`
      }
      return date
    },

    getPaymentStatusClass(status) {
      return `payment-${status}`
    },

    getPaymentStatusText(status) {
      const texts = {
        0: 'Offen',
        1: 'Anzahlung erforderlich',
        2: 'Bezahlt',
        3: 'Überfällig'
      }
      return texts[status] || 'Unbekannt'
    },

    getMaterialStatusClass(status) {
      if (!status) return ''
      if (status.toLowerCase().includes('verfügbar')) return 'material-available'
      if (status.toLowerCase().includes('bestellt')) return 'material-ordered'
      return 'material-pending'
    },

    getDeliveryMethodText(method) {
      const methods = {
        0: 'DPD',
        1: 'Spedition',
        2: 'Selbstabholer',
        3: 'Kurier',
        4: 'Post',
        5: 'UPS'
      }
      return methods[method] || 'Nicht angegeben'
    }
  }
}
</script>

<style scoped>
.detail-shift-panel { margin-top: 1rem; }

/* A-4 — Protokoll / Zeiten */
.protokoll-card .info-card-header { display: flex; align-items: center; gap: 0.5rem; }
.protokoll-pdf {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border: 1px solid #93c5fd; background: #dbeafe; color: #1e40af;
  border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer;
}
.protokoll-pdf:hover { background: #bfdbfe; }
.protokoll-cols { display: flex; flex-wrap: wrap; gap: 1.5rem; }
.protokoll-block { flex: 1 1 260px; min-width: 240px; }
.protokoll-block h4 { margin: 0 0 0.5rem; font-size: 0.9rem; color: #374151; }
.protokoll-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.protokoll-table td { padding: 0.3rem 0.4rem; border-bottom: 1px solid #eee; }
.protokoll-table td.pt-val { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.protokoll-table tr.pt-total td { font-weight: 700; border-top: 2px solid #d1d5db; border-bottom: none; }

/* Copy all styles from CustomerShow_Professional.vue */
.order-detail-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #f8f9fa;
  min-height: 100vh;
}

/* #3 WTV / Fremd-Weiterverarbeitung banner */
.wtv-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0 1.5rem 1rem;
  padding: 12px 14px;
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-left: 5px solid #d97706;
  border-radius: 8px;
  color: #92400e;
}
.wtv-banner > i {
  font-size: 1.15rem;
  color: #d97706;
  margin-top: 2px;
}
.wtv-content {
  flex: 1;
  font-size: 0.9rem;
  line-height: 1.4;
}
.wtv-content strong { color: #b45309; }
.wtv-text { margin-top: 2px; }
.wtv-dismiss {
  background: transparent;
  border: 0;
  cursor: pointer;
  color: #92400e;
  font-size: 1rem;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.12s;
}
.wtv-dismiss:hover { background: rgba(146,64,14,0.1); }

.header-flags { margin-right: 0.5rem; }

.btn-action.btn-storno { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
.btn-action.btn-storno:hover { background: #fecaca; border-color: #f87171; }
.btn-action.btn-versand { background: #dbeafe; border-color: #93c5fd; color: #1e40af; }
.btn-action.btn-versand:hover { background: #bfdbfe; border-color: #60a5fa; }

/* Versand-Modal (#18) */
.versand-modal { max-width: 560px; }
.versand-methods { display: flex; flex-wrap: wrap; gap: .5rem .9rem; margin-bottom: .5rem; }
.vm-radio { display: inline-flex; align-items: center; gap: .35rem; font-size: .85rem; font-weight: 500; }
.versand-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem .9rem; }
.versand-grid label { display: flex; flex-direction: column; font-size: .8rem; font-weight: 600; color: #374151; gap: .2rem; }
.versand-grid label.full { grid-column: 1 / -1; }
.versand-grid input { font: inherit; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 6px; }

.druckart-badge {
  display: inline-block;
  margin-right: 4px;
  padding: 1px 7px;
  border-radius: 4px;
  background: #1e293b;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
}

.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.storno-modal {
  background: #fff; border-radius: 12px; padding: 1.5rem; width: 420px; max-width: 92vw;
  box-shadow: 0 20px 50px rgba(0,0,0,.25);
}
.storno-modal h3 { margin: 0 0 .5rem; }
.storno-modal p { font-size: .9rem; color: #374151; }
.storno-modal label { display: block; font-size: .85rem; font-weight: 600; margin: .5rem 0 .25rem; }
.storno-modal .req { color: #dc2626; }
.storno-modal textarea { width: 100%; box-sizing: border-box; font: inherit; padding: 6px 8px; }
.storno-err { color: #dc2626; font-size: .85rem; }
.storno-actions { display: flex; justify-content: flex-end; gap: .5rem; margin-top: 1rem; }
.storno-actions button { padding: .5rem 1rem; border-radius: 8px; cursor: pointer; border: 1px solid #d1d5db; background: #fff; }
.btn-confirm { background: #dc2626; color: #fff; border-color: #dc2626; }
.btn-confirm:disabled { opacity: .5; cursor: default; }

/* Professional Header */
.page-header {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  border-bottom: 1px solid #f3f4f6;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
}

.back-button:hover {
  background: #f3f4f6;
  color: #374151;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-action:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.btn-action.btn-edit {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.btn-action.btn-edit:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-action.btn-print {
  color: #6b7280;
}

.header-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
}

.order-info {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.order-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.75rem;
  flex-shrink: 0;
}

.order-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
}

.invoice-suffix {
  color: #6b7280;
  font-weight: 400;
  font-size: 1rem;
  margin-left: 1rem;
}

.order-meta {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.meta-item i {
  color: #9ca3af;
}

.customer-link {
  color: #3b82f6;
  text-decoration: none;
  transition: color 0.2s;
}

.customer-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.status-badges {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

/* Status badges */
.status-badge, .priority-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

/* Status colors — exakte Legende aus Auftragsverlauf-BDE.pdf (vgl. globalOrderStyles.scss) */
.status-inquiry { background: #FE4638; color: #000; }
.status-ordered { background: #FF0000; color: #000; }
.status-prepress { background: #FFCC00; color: #000; }
.status-plates { background: #FFFF99; color: #000; }
.status-printing { background: #00FF00; color: #000; }
.status-processing { background: #CCFFFF; color: #000; }
.status-shipping { background: #7030A0; color: #fff; }
.status-delivered { background: #3366FF; color: #fff; }
.status-invoiced { background: #C0C0C0; color: #000; }
.status-archived { background: #E0E0E0; color: #333; }

/* Priority badges */
.priority-high { background: #fee2e2; color: #991b1b; }
.priority-medium { background: #fef3c7; color: #92400e; }
.priority-low { background: #d1fae5; color: #065f46; }

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  padding: 0 2rem 2rem;
  max-width: 1600px;
  margin: 0 auto;
}

.details-section, .relations-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Info Cards */
.info-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.info-card-header i {
  color: #6b7280;
  font-size: 1.1rem;
}

.info-card-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.info-card-body {
  padding: 1.5rem;
}

.info-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-row:first-child {
  padding-top: 0;
}

.info-label {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.info-value {
  color: #111827;
  font-size: 0.9rem;
  word-break: break-word;
}

.info-value.highlight {
  color: #3b82f6;
  font-weight: 600;
}

.no-delivery-banner {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #b91c1c;
  font-weight: 600;
  font-size: 0.9rem;
}

.info-link {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.2s;
}

.info-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

/* Contact items */
.contact-item {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.contact-item:last-child {
  margin-bottom: 0;
}

.contact-name {
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.contact-department {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.contact-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-link:hover {
  text-decoration: underline;
}

/* Paper items */
.paper-item {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.paper-item:last-child {
  margin-bottom: 0;
}

.paper-designation {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.paper-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.paper-supplier {
  font-size: 0.85rem;
  color: #6b7280;
}

.paper-date {
  font-size: 0.85rem;
  color: #3b82f6;
  margin-top: 0.25rem;
}

/* Badges */
.payment-badge, .material-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.payment-0 { background: #fee2e2; color: #991b1b; }
.payment-1 { background: #fef3c7; color: #92400e; }
.payment-2 { background: #d1fae5; color: #065f46; }
.payment-3 { background: #fecaca; color: #7f1d1d; }

.material-available { background: #d1fae5; color: #065f46; }
.material-ordered { background: #dbeafe; color: #1e40af; }
.material-pending { background: #fef3c7; color: #92400e; }

.customer-header h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
}

.customer-name-link {
  color: #111827;
  text-decoration: none;
  transition: color 0.2s;
}

.customer-name-link:hover {
  color: #3b82f6;
  text-decoration: underline;
}

.customer-suffix {
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 400;
}

.notes-text {
  color: #374151;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 0;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

/* Responsive */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .header-top, .header-main {
    padding: 1rem;
  }

  .header-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .order-info {
    flex-direction: column;
  }

  .status-badges {
    align-items: flex-start;
  }

  .content-grid {
    padding: 0 1rem 1rem;
  }

  .info-row {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
}
</style>
